import { environment } from '../environments/environment';

/**
 * Where the app points, decided when it LOADS rather than when it was built.
 *
 * A static SPA has no process to read environment variables from, which is why
 * v1 shipped one compiled `environment.<name>.ts` per environment. Here the
 * values come from `/config.json`, fetched once before Angular bootstraps and
 * written by the container from real environment variables (see
 * `scripts/render-config.sh`). One build then serves every environment.
 *
 * The compiled `environment` is the fallback, so `ng serve` and the tests
 * behave exactly as before when no `config.json` is served.
 */
export interface RuntimeConfig {
  /** agw-tracer-v2 base URL, no trailing slash. */
  tracerUrl: string;
  keycloakUrl: string;
  /** Realm pre-selected on the login screen; users can switch. */
  keycloakRealm: string;
  keycloakClientId: string;
  /** false skips Keycloak entirely — only for local UI work against an open tracer. */
  authEnabled: boolean;
  /** NDC API and BookingPad, used only for the "relaunch" deep links. */
  apiUrl: string;
  bpUrl: string;
}

export const runtimeConfig: RuntimeConfig = {
  tracerUrl: environment.tracerUrl,
  keycloakUrl: environment.keycloakUrl,
  keycloakRealm: environment.keycloakRealm,
  keycloakClientId: environment.keycloakClientId,
  authEnabled: environment.authEnabled,
  apiUrl: environment.apiUrl,
  bpUrl: environment.bpUrl,
};

const KEYS: (keyof RuntimeConfig)[] = [
  'tracerUrl',
  'keycloakUrl',
  'keycloakRealm',
  'keycloakClientId',
  'authEnabled',
  'apiUrl',
  'bpUrl',
];

/**
 * Load `/config.json` over the compiled defaults. Only known keys are taken,
 * and only when their type matches, so a malformed file degrades to the
 * defaults instead of breaking bootstrap. Never throws: a missing file is the
 * normal case in local development.
 */
export async function loadRuntimeConfig(): Promise<void> {
  try {
    const res = await fetch('config.json', { cache: 'no-store' });
    if (!res.ok) return;
    const raw: unknown = await res.json();
    if (!raw || typeof raw !== 'object') return;
    for (const key of KEYS) {
      const value = (raw as Record<string, unknown>)[key];
      if (typeof value === typeof runtimeConfig[key]) {
        (runtimeConfig as unknown as Record<string, unknown>)[key] =
          typeof value === 'string' ? value.replace(/\/+$/, '') : value;
      }
    }
  } catch {
    // No config.json, or unreachable: the compiled defaults stand.
  }
}
